/**
 * Cláusulas Contratuais Prontas para Prestadores Autônomos e MEI.
 * Solução 100% offline para proteção jurídica contra cancelamentos,
 * calotes, retrabalho não pago e reclamações fora do prazo legal.
 */

export type CategoriaClausula = 
  | 'garantia'
  | 'cancelamento'
  | 'escopo'
  | 'materiais'
  | 'limpeza'
  | 'portfolio'
  | 'pagamento';

export interface ClausulaContratual {
  id: string;
  titulo: string;
  descricaoCurta: string;
  icone: string;
  texto: string;
  categoria: CategoriaClausula;
}

export const CLAUSULAS_CONTRATUAIS_MEI: ClausulaContratual[] = [
  {
    id: 'garantia-90d',
    titulo: 'Garantia 90d (CDC)',
    descricaoCurta: 'Garantia legal de 90 dias sobre a mão de obra (Art. 26 do CDC)',
    icone: '🛡️',
    categoria: 'garantia',
    texto: '• GARANTIA LEGAL: Cobertura de 90 (noventa) dias sobre a mão de obra executada, contados a partir da entrega formal do serviço, nos termos do art. 26, inciso II, da Lei nº 8.078/1990 (Código de Defesa do Consumidor). A garantia não cobre desgaste natural, mau uso ou intervenções de terceiros.'
  },
  {
    id: 'retencao-sinal',
    titulo: 'Cancelamento & Sinal',
    descricaoCurta: 'Retenção do sinal em cancelamento unilateral com menos de 48h',
    icone: '⚡',
    categoria: 'cancelamento',
    texto: '• SINAL & CANCELAMENTO: Em caso de cancelamento imotivado por parte do contratante com menos de 48h de antecedência da data agendada, o valor do sinal será retido a título de perdas e danos e compensação pela reserva da data na agenda (art. 418 do Código Civil).'
  },
  {
    id: 'escopo-retrabalho',
    titulo: 'Escopo & Retrabalho',
    descricaoCurta: 'Serviços fora da descrição são orçados à parte',
    icone: '📝',
    categoria: 'escopo',
    texto: '• ESCOPO CONTRATUAL: A presente proposta abrange estritamente as tarefas descritas nos itens. Modificações no projeto, serviços adicionais ou imprevistos estruturais ocultos serão previamente orçados e formalizados mediante aprovação antes de sua execução.'
  },
  {
    id: 'quitacao-entrega',
    titulo: 'Quitação na Entrega',
    descricaoCurta: 'Saldo restante liquidado no ato da vistoria e entrega',
    icone: '🤝',
    categoria: 'pagamento',
    texto: '• QUITAÇÃO & ENTREGA: O saldo remanescente do orçamento deverá ser integralmente quitado no ato da conclusão e vistoria dos serviços, condição indispensável para a entrega de chaves, termos de garantia e liberação da obra.'
  },
  {
    id: 'materiais-cliente',
    titulo: 'Fornecimento de Materiais',
    descricaoCurta: 'Materiais fornecidos pelo cliente no local antes do início',
    icone: '📦',
    categoria: 'materiais',
    texto: '• MATERIAIS & INSUMOS: Os materiais de acabamento e peças a serem instaladas deverão estar disponíveis no local da execução na data de início, salvo os itens descritos expressamente como fornecidos pelo prestador.'
  },
  {
    id: 'limpeza-entulho',
    titulo: 'Limpeza & Descarte',
    descricaoCurta: 'Local limpo diariamente; caçamba e descarte pesado pelo cliente',
    icone: '🧹',
    categoria: 'limpeza',
    texto: '• LIMPEZA & RESÍDUOS: A área de trabalho será deixada limpa e varrida ao término das atividades diárias. O fornecimento de caçamba e a destinação final de entulho de demolição ou resíduos pesados são de responsabilidade do contratante.'
  },
  {
    id: 'portfolio-fotos',
    titulo: 'Fotos de Portfólio',
    descricaoCurta: 'Autorização de fotos/vídeos preservando privacidade',
    icone: '📸',
    categoria: 'portfolio',
    texto: '• REGISTRO EM PORTFÓLIO: O cliente autoriza o registro fotográfico do antes e depois dos serviços para fins exclusivos de divulgação em portfólio profissional e redes sociais, preservando a identidade, endereço e dados pessoais do contratante.'
  }
];

/**
 * Verifica se a cláusula já está presente no texto de observações.
 */
export function temClausula(textoAtual: string, clausulaTextoOuId: string): boolean {
  if (!textoAtual) return false;
  const c = CLAUSULAS_CONTRATUAIS_MEI.find(item => item.id === clausulaTextoOuId || item.texto === clausulaTextoOuId);
  const textoParaBuscar = c ? c.texto : clausulaTextoOuId;
  return textoAtual.includes(textoParaBuscar.trim());
}

/**
 * Adiciona a cláusula ao final do texto de observações se ainda não estiver presente.
 */
export function adicionarClausula(textoAtual: string, clausulaTextoOuId: string): string {
  const c = CLAUSULAS_CONTRATUAIS_MEI.find(item => item.id === clausulaTextoOuId || item.texto === clausulaTextoOuId);
  const textoParaAdicionar = (c ? c.texto : clausulaTextoOuId).trim();
  
  if (!textoAtual || textoAtual.trim() === '') {
    return textoParaAdicionar;
  }
  
  if (temClausula(textoAtual, textoParaAdicionar)) {
    return textoAtual;
  }
  
  return `${textoAtual.trim()}\n\n${textoParaAdicionar}`;
}

/**
 * Remove a cláusula do texto de observações se estiver presente.
 */
export function removerClausula(textoAtual: string, clausulaTextoOuId: string): string {
  if (!textoAtual) return '';
  const c = CLAUSULAS_CONTRATUAIS_MEI.find(item => item.id === clausulaTextoOuId || item.texto === clausulaTextoOuId);
  const textoParaRemover = (c ? c.texto : clausulaTextoOuId).trim();
  
  let resultado = textoAtual.replace(textoParaRemover, '').trim();
  // Normalizar quebras de linha múltiplas residuais
  resultado = resultado.replace(/\n{3,}/g, '\n\n');
  return resultado;
}

/**
 * Alterna (adiciona se não tiver, remove se tiver) a cláusula nas observações.
 */
export function alternarClausula(textoAtual: string, clausulaTextoOuId: string): string {
  if (temClausula(textoAtual, clausulaTextoOuId)) {
    return removerClausula(textoAtual, clausulaTextoOuId);
  }
  return adicionarClausula(textoAtual, clausulaTextoOuId);
}
